import * as React from 'react'
import Head from 'next/head'
import { getCars, getPosts } from '../asyncLoaders/buttercms'
import Layout from '../components/Layout'
import { Container } from '../components/Container'
import { SitemapSection } from '../components/SitemapPage/SitemapSection'
import * as R from 'ramda'
import builder from '@builder.io/react'
import { services } from '../components/Navbar'

builder.init(process.env.BUILDER_API_KEY)

export type SitemapSectionItem = {
  title: string
  path: string
}

export type SitemapSection = {
  title: string
  items: SitemapSectionItem[]
}

type SitemapProps = {
  preview?: boolean
  sections: SitemapSection[]
}

const Sitemap = ({ sections, preview }: SitemapProps) => {
  return (
    <Layout title={'Site Map - NuBrakes Mobile Brake Repair'} preview={preview}>
      <Head>
        <meta name="description" content="Nubrakes sitemap." />
        <title>Site Map - NuBrakes Mobile Brake Repair</title>
      </Head>
      <section>
        <Container>
          <h1 className="mb-8">Sitemap</h1>
          {sections.map((section) => {
            return <SitemapSection key={section.title} title={section.title} items={section.items} />
          })}
        </Container>
      </section>
    </Layout>
  )
}

const generateSections = ({
  blogSection,
  carsSection,
  citiesSections,
  servicesSections,
}: {
  blogSection: SitemapSection
  carsSection: SitemapSection
  citiesSections: SitemapSection[]
  servicesSections: SitemapSection[]
}): SitemapSection[] => {
  return [
    {
      title: 'Company',
      items: [
        {
          title: 'About Us',
          path: '/about-us',
        },
        {
          title: 'Reviews',
          path: '/reviews',
        },
        {
          title: 'Contact Us',
          path: '/contact',
        },
        {
          title: 'Warranty Policy',
          path: '/warranty',
        },
        {
          title: 'Careers',
          path: 'https://apply.workable.com/nubrakes',
        },
        {
          title: 'Become A Technician',
          path: 'https://www.classet.org/org/nubrakes',
        },
        {
          title: 'Privacy Policy',
          path: '/privacy-policy',
        },
        {
          title: 'Terms and Conditions',
          path: '/terms-conditions',
        },
      ],
    },
    {
      title: 'Get a Service',
      items: [
        {
          title: 'Pricing',
          path: '/pricing',
        },
        {
          title: 'Cities',
          path: '/cities',
        },
        {
          title: 'Financing',
          path: '/financing',
        },
        {
          title: 'Fleet Service',
          path: '/fleet-service',
        },
        {
          title: 'Break Repair Near Me',
          path: '/break-repair-near-me',
        },
        {
          title: 'Schedule A Repair',
          path: '/get-quote',
        },
      ],
    },
    ...citiesSections,
    blogSection,
    ...servicesSections,
    carsSection,
  ]
}

export default Sitemap

const getBlogSection = async (): Promise<SitemapSection> => {
  const posts = (await getPosts()) || []
  const postsItems: SitemapSectionItem[] = posts.map((post) => {
    return {
      title: post.title,
      path: `/blog/${post.slug}`,
    }
  })
  return {
    title: 'Blog Posts',
    items: [
      {
        title: 'All Posts',
        path: '/blog',
      },
      ...postsItems,
    ],
  }
}

const getCarsSection = async (): Promise<SitemapSection> => {
  const cars = ((await getCars()) || []) as Array<{
    makeLongName: string
    makeShortName: string
    slug: string
  }>
  return {
    title: 'Cars',
    items: cars.map((car) => ({
      title: car.makeShortName.charAt(0).toUpperCase() + car.makeShortName.slice(1),
      path: `/cars/${car.slug}`,
    })),
  }
}

const getServicesSections = async (): Promise<SitemapSection[]> => {
  const servicesGrouped = R.groupBy((service) => service.groupName, services)

  const servicesSections = R.pipe(
    R.toPairs<
      {
        linkTitle: string
        link: string
        groupName: string
      }[]
    >,
    R.map(([key, value]) => {
      return {
        title: `Auto Services - ${key}`,
        items: value.map((s) => ({
          title: s.linkTitle,
          path: s.link,
        })),
      }
    })
  )(servicesGrouped)

  return servicesSections
}

const getCitiesSections = async (context: any): Promise<SitemapSection[]> => {
  const targeting = { urlPath: '_', device: '_' } as any
  const path = `/cities/${(context.params?.cities as string[])?.join('/') || ''}`
  const defaultPropOptions = {
    userAttributes: {
      ...targeting,
      urlPath: path,
    },
  }
  const builderResults = (await builder.get('cities', defaultPropOptions).promise()) || null
  const builderData = builderResults?.data?.state || {}
  const builderCityData: Record<string, any> = Object.keys(builderData)
    .filter((key) => key.includes('cityData'))
    .reduce((obj, key) => {
      obj[key] = builderData[key]
      return obj
    }, {})

  const citiesSections: SitemapSection[] = R.pipe(
    R.toPairs<{
      results: Array<{
        data: {
          cityName: string
          stateAbbreviation: string
          stateName: string
          slug: string
        }
      }>
    }>,
    (pairs) => R.sortBy<any>(R.prop<any>(0), pairs),
    R.map((pair) => {
      const citiesItems: SitemapSectionItem[] = pair[1]?.results?.map(({ data }) => {
        return {
          title: data.cityName,
          path: `/cities/${data.slug}`,
        }
      })
      const stateAbbreviation = pair[1]?.results?.[0]?.data?.stateAbbreviation
      return {
        title: `Cities - ${stateAbbreviation}`,
        items: citiesItems,
      }
    })
  )(builderCityData)

  return citiesSections
}

export const getStaticProps = async (context) => {
  const preview = context.preview || false

  const blogSection = await getBlogSection()

  const carsSection = await getCarsSection()

  const citiesSections = await getCitiesSections(context)

  const servicesSections = await getServicesSections()

  const sections = generateSections({
    blogSection,
    carsSection,
    citiesSections,
    servicesSections,
  })

  return {
    props: {
      sections,
      preview,
    },
  }
}
